import { useMemo, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import {
  useCompetitors,
  useEventGraph,
  type EventGraphEdge,
  type EventGraphNode,
} from '../../hooks/useCompetitiveData'

const DAY_OPTIONS = [7, 14, 30] as const

const NODE_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'anomaly_event', label: 'Anomaly' },
  { value: 'viral_moment', label: 'Viral' },
  { value: 'bsr_shift', label: 'BSR' },
  { value: 'creator_surge', label: 'Creator' },
]

function formatDateLabel(value: string): string {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    weekday: 'short',
  })
}

function severityBadgeClasses(severity: string | null | undefined): string {
  const normalized = (severity ?? '').toLowerCase()
  if (normalized === 'critical') {
    return 'bg-red-100 border-red-400 text-red-700'
  }
  if (normalized === 'high') {
    return 'bg-orange-100 border-orange-400 text-orange-700'
  }
  if (normalized === 'medium') {
    return 'bg-yellow-100 border-yellow-400 text-yellow-800'
  }
  return 'bg-slate-100 border-slate-300 text-slate-700'
}

function nodeTypeLabel(value: string): string {
  if (value === 'anomaly_event') return 'Anomaly'
  if (value === 'viral_moment') return 'Viral'
  if (value === 'bsr_shift') return 'BSR'
  if (value === 'creator_surge') return 'Creator'
  return value
}

function metadataRecord(node: EventGraphNode): Record<string, unknown> {
  return node.metadata && typeof node.metadata === 'object' ? (node.metadata as Record<string, unknown>) : {}
}

function isUnknownCompetitor(value: string): boolean {
  const normalized = value.trim().toLowerCase()
  return normalized === 'unknown competitor' || normalized === 'unknown' || normalized.startsWith('unknown')
}

function isUnknownKeywordNode(node: EventGraphNode): boolean {
  const metadata = metadataRecord(node)
  const anomalyType = String(metadata.anomaly_type ?? '').toLowerCase()
  const label = String(node.entity_label ?? '').toLowerCase()
  return anomalyType.includes('keyword') || label.includes('keyword')
}

function extractKeywordOrAsin(node: EventGraphNode): string | null {
  const metadata = metadataRecord(node)
  const directCandidates = [metadata.keyword, metadata.query, metadata.query_text, metadata.asin]
  for (const candidate of directCandidates) {
    const value = String(candidate ?? '').trim()
    if (value) {
      return value
    }
  }

  const description = String(metadata.description ?? node.entity_label ?? '')
  const quotedMatch = description.match(/'([^']+)'/)
  if (quotedMatch?.[1]) {
    return quotedMatch[1].trim()
  }

  const asinMatch = description.match(/\b[A-Z0-9]{10}\b/)
  if (asinMatch?.[0]) {
    return asinMatch[0]
  }

  return null
}

function competitorGroupClasses(competitorId: number | null, fallbackIndex: number): string {
  if (competitorId === null || competitorId === undefined) {
    return 'border-slate-200 bg-slate-50'
  }

  const palette = [
    'border-blue-200 bg-blue-50',
    'border-emerald-200 bg-emerald-50',
    'border-amber-200 bg-amber-50',
    'border-rose-200 bg-rose-50',
    'border-violet-200 bg-violet-50',
    'border-cyan-200 bg-cyan-50',
  ]

  const normalizedIndex = Math.abs((competitorId + fallbackIndex) % palette.length)
  return palette[normalizedIndex]
}

function buildEdgeLookup(edges: EventGraphEdge[]): Map<number, Set<string>> {
  const lookup = new Map<number, Set<string>>()

  for (const edge of edges) {
    for (const nodeId of [edge.source_node_id, edge.target_node_id]) {
      if (!lookup.has(nodeId)) {
        lookup.set(nodeId, new Set())
      }
      lookup.get(nodeId)?.add(edge.edge_type)
    }
  }

  return lookup
}

function groupByDate(nodes: EventGraphNode[]): Array<[string, EventGraphNode[]]> {
  const grouped = new Map<string, EventGraphNode[]>()

  for (const node of nodes) {
    const key = String(node.event_date)
    if (!grouped.has(key)) {
      grouped.set(key, [])
    }
    grouped.get(key)?.push(node)
  }

  return Array.from(grouped.entries()).sort((left, right) => right[0].localeCompare(left[0]))
}

export default function EventGraphPanel() {
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [selectedCompetitorId, setSelectedCompetitorId] = useState<string>('all')
  const [selectedNodeType, setSelectedNodeType] = useState<string>('all')
  const [daysIndex, setDaysIndex] = useState<number>(1)
  const [hideUnknownCompetitors, setHideUnknownCompetitors] = useState<boolean>(true)

  const days = DAY_OPTIONS[Math.max(0, Math.min(DAY_OPTIONS.length - 1, daysIndex))]

  const { data: competitorsResponse } = useCompetitors()
  const competitors = competitorsResponse?.competitors ?? []

  const { data, isLoading, error } = useEventGraph({
    days,
    competitorId: selectedCompetitorId === 'all' ? undefined : selectedCompetitorId,
    nodeType: selectedNodeType === 'all' ? undefined : selectedNodeType,
  })

  const nodes = data?.nodes ?? []
  const edges = data?.edges ?? []
  const hasSummaryData = Array.isArray(data?.nodes)
  const competitorCount = useMemo(() => {
    const unique = new Set<string>()
    for (const node of nodes) {
      const name = String(node.competitor_name ?? '').trim()
      if (!name || isUnknownCompetitor(name)) {
        continue
      }
      unique.add(name.toLowerCase())
    }
    return unique.size
  }, [nodes])
  const summaryText = hasSummaryData
    ? `${nodes.length} event${nodes.length === 1 ? '' : 's'} across ${competitorCount} competitor${competitorCount === 1 ? '' : 's'} in the last ${days} days`
    : error instanceof Error
      ? 'Activity summary unavailable.'
      : `Loading activity for the last ${days} days...`

  const edgeLookup = useMemo(() => buildEdgeLookup(edges), [edges])
  const dateGroups = useMemo(() => groupByDate(nodes), [nodes])
  const visibleDateGroups = useMemo(() => {
    return dateGroups
      .map(([dateKey, dateNodes]) => {
        const competitorGroups = new Map<string, EventGraphNode[]>()
        for (const node of dateNodes) {
          const key = node.competitor_name || 'Unknown competitor'
          if (!competitorGroups.has(key)) {
            competitorGroups.set(key, [])
          }
          competitorGroups.get(key)?.push(node)
        }

        const sortedGroups = Array.from(competitorGroups.entries()).sort(([leftName], [rightName]) => {
          const leftUnknown = isUnknownCompetitor(leftName) ? 1 : 0
          const rightUnknown = isUnknownCompetitor(rightName) ? 1 : 0
          if (leftUnknown !== rightUnknown) {
            return leftUnknown - rightUnknown
          }
          return leftName.localeCompare(rightName)
        })

        const filteredGroups = hideUnknownCompetitors
          ? sortedGroups.filter(([competitorName]) => !isUnknownCompetitor(competitorName))
          : sortedGroups

        return [dateKey, filteredGroups] as const
      })
      .filter(([, groups]) => groups.length > 0)
  }, [dateGroups, hideUnknownCompetitors])

  const renderNodeCard = (node: EventGraphNode, labelOverride?: string) => {
    const nodeEdges = edgeLookup.get(node.id)
    const hasBurst = nodeEdges?.has('same_competitor_burst') ?? false
    const hasResponse = nodeEdges?.has('competitor_response') ?? false

    return (
      <div key={`${node.id}-${labelOverride ?? 'default'}`} className="rounded-lg border border-white/80 bg-white/80 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-semibold capitalize ${severityBadgeClasses(
              node.severity
            )}`}
          >
            {node.severity || 'low'}
          </span>
          <span className="inline-flex rounded-md border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
            {nodeTypeLabel(node.node_type)}
          </span>
          {hasBurst && (
            <span className="inline-flex rounded-md border border-orange-200 bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
              🔥 Burst
            </span>
          )}
          {hasResponse && (
            <span className="inline-flex rounded-md border border-blue-200 bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
              ⚡ Response
            </span>
          )}
        </div>
        <p className="mt-2 text-sm font-medium text-slate-800">{labelOverride || node.entity_label}</p>
      </div>
    )
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <button
        type="button"
        onClick={() => setIsExpanded((current) => !current)}
        className="flex w-full items-center justify-between gap-3 border-b border-[var(--gray-200)] pb-2 text-left"
        aria-expanded={isExpanded}
      >
        <span className="flex items-center gap-2">
          <ChevronRight className={`h-4 w-4 text-[var(--gray-500)] transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-[var(--gray-400)]">Activity Log</span>
        </span>
        <span className={`competitive-body-text ${hasSummaryData ? '' : 'text-slate-500'}`}>{summaryText}</span>
      </button>

      {isExpanded && (
        <div className="mt-4 max-h-[500px] overflow-y-auto pr-1">
          <div className="mb-5 grid grid-cols-1 gap-3 lg:grid-cols-4">
            <label className="space-y-1">
              <span className="competitive-support-label">Competitor</span>
              <select
                value={selectedCompetitorId}
                onChange={(event) => setSelectedCompetitorId(event.target.value)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="all">All Competitors</option>
                {competitors.map((competitor) => (
                  <option key={competitor.id} value={String(competitor.id)}>
                    {competitor.brand_name}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-1">
              <span className="competitive-support-label">Event Type</span>
              <select
                value={selectedNodeType}
                onChange={(event) => setSelectedNodeType(event.target.value)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {NODE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-1">
              <span className="competitive-support-label">Days ({days})</span>
              <input
                type="range"
                min={0}
                max={DAY_OPTIONS.length - 1}
                step={1}
                value={daysIndex}
                onChange={(event) => setDaysIndex(Number.parseInt(event.target.value, 10) || 0)}
                className="w-full accent-[var(--patterniq-navy-900)]"
              />
              <div className="flex justify-between text-xs text-slate-500">
                {DAY_OPTIONS.map((option) => (
                  <span key={option}>{option}d</span>
                ))}
              </div>
            </label>

            <label className="space-y-1">
              <span className="competitive-support-label">Noise Filter</span>
              <button
                type="button"
                onClick={() => setHideUnknownCompetitors((current) => !current)}
                className={`w-full rounded-md border px-3 py-2 text-left text-sm font-medium ${
                  hideUnknownCompetitors
                    ? 'border-[var(--gray-300)] bg-[var(--gray-100)] text-[var(--gray-700)]'
                    : 'border-slate-300 bg-white text-slate-700'
                }`}
              >
                {hideUnknownCompetitors ? 'Hide unknown competitors: ON' : 'Hide unknown competitors: OFF'}
              </button>
            </label>
          </div>

          {error instanceof Error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error.message}</div>
          )}

          {isLoading ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">Loading event graph...</div>
          ) : visibleDateGroups.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
              No notable competitor events this period.
            </div>
          ) : (
            <div className="space-y-4">
              {visibleDateGroups.map(([dateKey, competitorGroups]) => {
                return (
                  <div key={dateKey} className="grid grid-cols-1 gap-3 border-b border-slate-100 pb-4 md:grid-cols-[150px_1fr]">
                    <div className="text-sm font-semibold text-slate-700">{formatDateLabel(dateKey)}</div>
                    <div className="space-y-3">
                      {competitorGroups.map(([competitorName, competitorNodes], groupIndex) => {
                        const unknownGroup = isUnknownCompetitor(competitorName)
                        const unknownKeywordEntries = competitorNodes
                          .filter((node) => isUnknownKeywordNode(node))
                          .map((node) => ({ node, identifier: extractKeywordOrAsin(node) }))
                        const unknownByIdentifier = new Map<string, EventGraphNode[]>()
                        const unknownWithoutIdentifier: EventGraphNode[] = []
                        const nonUnknownNodes = competitorNodes.filter((node) => !isUnknownKeywordNode(node))

                        for (const entry of unknownKeywordEntries) {
                          if (!entry.identifier) {
                            unknownWithoutIdentifier.push(entry.node)
                            continue
                          }
                          const key = entry.identifier
                          if (!unknownByIdentifier.has(key)) {
                            unknownByIdentifier.set(key, [])
                          }
                          unknownByIdentifier.get(key)?.push(entry.node)
                        }

                        return (
                          <div
                            key={`${dateKey}-${competitorName}`}
                            className={`rounded-xl border p-3 ${competitorGroupClasses(
                              competitorNodes[0]?.competitor_id ?? null,
                              groupIndex
                            )}`}
                          >
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-700">{competitorName}</p>
                            <div className="space-y-2">
                              {unknownGroup ? null : competitorNodes.map((node) => renderNodeCard(node))}
                              {unknownGroup &&
                                nonUnknownNodes.map((node) => renderNodeCard(node))}
                              {unknownGroup &&
                                Array.from(unknownByIdentifier.entries()).map(([identifier, nodesForIdentifier]) =>
                                  nodesForIdentifier.length > 1
                                    ? renderNodeCard(
                                        nodesForIdentifier[0],
                                        `${nodesForIdentifier.length} unknown products appeared on '${identifier}' this week`
                                      )
                                    : renderNodeCard(
                                        nodesForIdentifier[0],
                                        `Unknown — appeared on '${identifier}'`
                                      )
                                )}
                              {unknownGroup && unknownWithoutIdentifier.length > 0 ? (
                                unknownWithoutIdentifier.length > 1 ? (
                                  renderNodeCard(
                                    unknownWithoutIdentifier[0],
                                    `${unknownWithoutIdentifier.length} unknown competitor events without keyword details`
                                  )
                                ) : (
                                  renderNodeCard(unknownWithoutIdentifier[0])
                                )
                              ) : null}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </section>
  )
}
